import React, {
    useEffect,
    useRef,
    memo,
    useMemo,
    useState,
    useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
    Bot,
    Loader2,
    Code,
    Globe,
    Archive,
    Minus,
    Check,
    FileText,
    Lightbulb,
    ChevronDown,
    ChevronUp,
    Search,
    Edit,
    Trash2,
    PlayCircle,
    CheckCircle,
    XCircle,
    Wrench,
    ThumbsUp,
    ThumbsDown,
    Meh,
    Star,
    GitCompareArrows,
    Database,
    Eye,
    Terminal,
    Plus,
    Settings,
    Zap,
    List,
    Scissors,
    MessageSquare,
    X,
    Key,
    AlertTriangle,
    Table,
} from "lucide-react";
import Markdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatDistanceToNow } from "date-fns";
import Icon from "../components/Icon";

import DiffDisplay from "../components/DiffDisplay";
import DiffModal from "../components/DiffModal";
import { useAuth } from "../context/AuthContext";
import { useWorkflowGeneration } from "../context/WorkflowGenerationContext";
import { useWorkflow } from "../context/WorkflowContext";
import { useCodeGeneration } from "../context/CodeGenerationContext";
import TaskList from "../components/TaskList";
import { useChatContext } from "./ChatContext";
import { CodeGenerationFeedback } from "../components/CodeGenerationFeedback";
import BrowserAgentPreview from "../components/BrowserAgentPreview";
import HumanSupportMessage from "./components/HumanSupportMessage";
import UserActionRequest from "./components/UserActionRequest";
import MessageUsageDropdown from "./components/MessageUsageDropdown";
import RollbackButton from "./components/RollbackButton";
import LongThreadNudge from "./components/LongThreadNudge";
import {
    ClickableImage,
    ImageGalleryPreview,
    ImageLightboxDialog,
} from "./components/ImageLightbox";

const BREAK_WORD_STYLE = {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
};

const safeUrlTransform = (url, key, node) => {
    if (url == null) return "";
    return defaultUrlTransform(String(url), key, node);
};

const RuntimeDataTable = memo(function RuntimeDataTable({ data }) {
    const entries = useMemo(() => Object.entries(data), [data]);
    return (
        <table className="mt-1 text-xs text-gray-500 border-collapse">
            <tbody>
                {entries.map(([k, v]) => (
                    <tr key={k}>
                        <td className="pr-3 py-0.5 text-gray-400 align-top whitespace-nowrap">
                            {k}
                        </td>
                        <td className="py-0.5" style={BREAK_WORD_STYLE}>
                            {typeof v === "object"
                                ? JSON.stringify(v)
                                : String(v)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
});

const PRE_STYLE = {
    overflowWrap: "anywhere",
};

const getSystemBgColor = (systemType) => {
    switch (systemType) {
        case "error":
            return "bg-red-50 border-red-200";
        case "success":
            return "bg-green-50 border-green-200";
        case "warning":
            return "bg-yellow-50 border-yellow-200";
        default:
            return "transparent border-none";
    }
};

const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
};

const FileCard = ({ file }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-white border border-gray-300 rounded-lg p-3 hover:border-blue-400 transition-colors">
            <div className="flex items-center gap-3">
                <div className="flex-shrink-0 bg-blue-100 rounded-lg p-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <div
                        className="font-medium text-gray-900 text-sm break-words"
                        style={BREAK_WORD_STYLE}
                    >
                        {file?.name || t("chatMessages.file")}
                    </div>
                    {file?.size && (
                        <div className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const FileListPreview = ({ files }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    if (!files || files.length === 0) return null;
    if (files.length === 1) return <FileCard file={files[0]} />;

    if (!expanded) {
        return (
            <div className="space-y-2">
                <FileCard file={files[0]} />
                <button
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(true);
                    }}
                >
                    +{files.length - 1} {t("chatMessages.moreFiles")}
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
                {files.map((file, index) => (
                    <FileCard key={file.id || index} file={file} />
                ))}
            </div>
            <button
                className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1"
                onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(false);
                }}
            >
                {t("chatMessages.showLess")}
            </button>
        </div>
    );
};

// Map tool names to user-friendly descriptions and icons
const getToolDisplayInfo = (toolName, t) => {
    const toolMap = {
        // File operations
        read_file: {
            label: t("chatMessages.toolLookedAtFile"),
            icon: FileText,
            color: "indigo",
        },
        read_files: {
            label: t("chatMessages.toolLookedAtFiles"),
            icon: FileText,
            color: "indigo",
        },
        create_file: {
            label: t("chatMessages.toolCreatedFile"),
            icon: Code,
            color: "emerald",

        },
        update_file: {
            label: t("chatMessages.toolUpdatedFile"),
            icon: Edit,
            color: "teal",

        },
        delete_file: {
            label: t("chatMessages.toolRemovedFile"),
            icon: Trash2,
            color: "red",

        },
        rename_file: {
            label: t("chatMessages.toolRenamedFile"),
            icon: Edit,
            color: "purple",

        },
        // Search operations
        grep_search: {
            label: t("chatMessages.toolSearchedInCode"),
            icon: Search,
            color: "pink",
        },
        glob_search: {
            label: t("chatMessages.toolFoundFiles"),
            icon: Search,
            color: "orange",
        },
        file_search: {
            label: t("chatMessages.toolFoundFiles"),
            icon: Search,
            color: "orange",
        },
        search_in_files: {
            label: t("chatMessages.toolSearchedInFiles"),
            icon: Search,
            color: "pink",
        },
        read_application_logs: {
            label: t("chatMessages.toolCheckedLogs"),
            icon: FileText,
            color: "gray",
        },
        // Code operations
        code_generation: {
            label: t("chatMessages.toolGeneratedCode"),
            icon: Code,
            color: "emerald",
        },
        line_replace: {
            label: t("chatMessages.toolMadeChanges"),
            icon: Edit,
            color: "teal",

        },
        edit_file: {
            label: t("chatMessages.toolEditedFile"),
            icon: Edit,
            color: "teal",

        },
        execute_code: {
            label: t("chatMessages.toolRanCode"),
            icon: PlayCircle,
            color: "amber",
        },
        // Planning operations
        start_code_generation: {
            label: t("chatMessages.toolStartedBuilding"),
            icon: Code,
            color: "cyan",
        },
        todo_write: {
            label: t("chatMessages.toolUpdatedTasks"),
            icon: Check,
            color: "blue",
        },
        planning: {
            label: t("chatMessages.toolPlannedWork"),
            icon: Lightbulb,
            color: "violet",
        },
        // Subagent operations
        explore_codebase: {
            label: t("chatMessages.toolExploring"),
            icon: Search,
            color: "purple",
        },
        answer_platform_question: {
            label: t("chatMessages.toolAnswering"),
            icon: Search,
            color: "purple",
        },
        report_user_feedback: {
            label: t("chatMessages.toolSendingFeedback"),
            icon: MessageSquare,
            color: "orange",
        },
        search_api: {
            label: t("chatMessages.toolApiSearch"),
            icon: Search,
            color: "cyan",
        },
        search_web: {
            label: t("chatMessages.toolWebSearch"),
            icon: Globe,
            color: "indigo",
        },
        browser_agent: {
            label: t("chatMessages.toolBrowserAgent"),
            icon: Globe,
            color: "purple",
        },
        view_version_diff: {
            label: t("chatMessages.toolComparingVersions"),
            icon: GitCompareArrows,
            color: "orange",
        },
        // Automation Agent tools
        inspect_step_data: {
            label: t("chatMessages.toolInspectedData"),
            icon: Database,
            color: "cyan",
        },
        inspect_execution_logs: {
            label: t("chatMessages.toolCheckedExecutionLogs"),
            icon: Terminal,
            color: "gray",
        },
        read_step_code: {
            label: t("chatMessages.toolReadStepCode"),
            icon: Eye,
            color: "indigo",
        },
        edit_step_code: {
            label: t("chatMessages.toolEditedStepCode"),
            icon: Edit,
            color: "teal",
        },
        grep_step_code: {
            label: t("chatMessages.toolSearchedStepCode"),
            icon: Search,
            color: "pink",
        },
        generate_code: {
            label: t("chatMessages.toolGeneratedCode"),
            icon: Code,
            color: "emerald",
        },
        create_step: {
            label: t("chatMessages.toolCreatedStep"),
            icon: Plus,
            color: "emerald",
        },
        list_steps: {
            label: t("chatMessages.toolListedSteps"),
            icon: List,
            color: "blue",
        },
        delete_step: {
            label: t("chatMessages.toolDeletedStep"),
            icon: Trash2,
            color: "red",
        },
        update_step_metadata: {
            label: t("chatMessages.toolUpdatedStepMetadata"),
            icon: Settings,
            color: "purple",
        },
        preview_execution: {
            label: t("chatMessages.toolPreviewedExecution"),
            icon: PlayCircle,
            color: "amber",
        },
        generate_trigger: {
            label: t("chatMessages.toolGeneratedTrigger"),
            icon: Zap,
            color: "orange",
        },
        list_triggers: {
            label: t("chatMessages.toolListedTriggers"),
            icon: List,
            color: "blue",
        },
        search_tools: {
            label: t("chatMessages.toolSearchedTools"),
            icon: Search,
            color: "cyan",
        },
        list_secrets: {
            label: t("chatMessages.toolListedSecrets"),
            icon: Key,
            color: "amber",
        },
        request_secret: {
            label: t("chatMessages.toolRequestedSecret"),
            icon: Key,
            color: "amber",
        },
        fetch_documentation: {
            label: t("chatMessages.toolFetchedDocs"),
            icon: Globe,
            color: "indigo",
        },
        enter_plan_mode: {
            label: t("chatMessages.toolEnteredPlanMode"),
            icon: Lightbulb,
            color: "violet",
        },
        exit_plan_mode: {
            label: t("chatMessages.toolExitedPlanMode"),
            icon: Lightbulb,
            color: "violet",
        },
        report_user_frustration: {
            label: t("chatMessages.toolReportedFrustration"),
            icon: AlertTriangle,
            color: "orange",
        },
        // App tools — discoverable
        testing_agent: {
            label: t("chatMessages.toolTestingAgent"),
            icon: PlayCircle,
            color: "emerald",
        },
        view_chat_history: {
            label: t("chatMessages.toolViewedChatHistory"),
            icon: MessageSquare,
            color: "blue",
        },
        search_creative_solutions: {
            label: t("chatMessages.toolSearchedSolutions"),
            icon: Lightbulb,
            color: "amber",
        },
        explore_spreadsheet: {
            label: t("chatMessages.toolExploredSpreadsheet"),
            icon: Table,
            color: "emerald",
        },
        explore_database: {
            label: t("chatMessages.toolExploredDatabase"),
            icon: Database,
            color: "violet",
        },
        // Other registered tools
        create_records: {
            label: t("chatMessages.toolCreatedRecords"),
            icon: Database,
            color: "cyan",
        },
        search_api_prompts: {
            label: t("chatMessages.toolSearchedApiPrompts"),
            icon: Search,
            color: "cyan",
        },
        report_planned_items: {
            label: t("chatMessages.toolReportedPlan"),
            icon: Lightbulb,
            color: "violet",
        },
        read_skill: {
            label: t("chatMessages.toolReadSkill"),
            icon: FileText,
            color: "purple",
        },
        list_skills: {
            label: t("chatMessages.toolListedSkills"),
            icon: List,
            color: "purple",
        },
        request_user_action: {
            label: t("chatMessages.toolRequestedUserAction"),
            icon: MessageSquare,
            color: "blue",
        },
        // Default
        default: {
            label: t("chatMessages.toolWorkedOn"),
            icon: Wrench,
            color: "gray",
        },
    };

    return toolMap[toolName] || toolMap.default;
};

// Helper to format tool details in a user-friendly way (uses snapshot data from backend)
const formatToolDetails = (toolName, args, result, t) => {
    const details = [];

    switch (toolName) {
        case "read_file":
            if (args?.file_path) {
                details.push({
                    label: t("chatMessages.detailFile"),
                    value: args.file_path,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            if (result?.file?.totalLines !== undefined) {
                const lines =
                    result.file.linesReturned ?? result.file.totalLines;
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: `Read ${lines} of ${result.file.totalLines} lines`,
                });
            }
            break;

        case "read_files":
            if (args?.files?.length) {
                details.push({
                    label: t("chatMessages.detailFiles"),
                    value: args.files.join(", "),
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            if (result?.filesRead !== undefined) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: `Read ${result.filesRead} of ${
                        result.totalRequested || args?.files?.length || 0
                    } files`,
                });
            }
            break;

        case "create_file":
        case "update_file":
            if (args?.fileName) {
                details.push({
                    label: t("chatMessages.detailFile"),
                    value: args.fileName,
                });
            }
            if (result?.codeLength) {
                details.push({
                    label: t("chatMessages.detailSize"),
                    value: `${result.codeLength} characters`,
                });
            }
            break;

        case "delete_file":
            if (args?.fileName) {
                details.push({
                    label: t("chatMessages.detailFile"),
                    value: args.fileName,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            break;

        case "grep_search":
        case "file_search":
            if (args?.pattern || args?.searchTerm) {
                details.push({
                    label: t("chatMessages.detailSearch"),
                    value: `"${args.pattern || args.searchTerm}"`,
                });
            }
            if (args?.glob) {
                details.push({
                    label: t("chatMessages.detailIn"),
                    value: args.glob,
                });
            }
            if (result?.totalMatches !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.totalMatches} matches in ${
                        result.filesWithMatches || 0
                    } files`,
                });
            }
            break;

        case "glob_search":
            if (args?.pattern) {
                details.push({
                    label: t("chatMessages.detailPattern"),
                    value: `"${args.pattern}"`,
                });
            }
            if (result?.matchCount !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.matchCount} file${
                        result.matchCount !== 1 ? "s" : ""
                    }`,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            break;

        case "search_api":
            if (args?.query) {
                details.push({
                    label: t("chatMessages.detailQuery"),
                    value: args.query,
                });
            }
            if (args?.integration) {
                details.push({
                    label: t("chatMessages.detailApi"),
                    value: args.integration,
                });
            }
            if (result?.exampleCount !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.exampleCount} examples`,
                });
            }
            break;

        case "execute_code":
            if (args?.description) {
                details.push({
                    label: t("chatMessages.detailRunning"),
                    value: args.description,
                });
            }
            if (result?.hasOutput) {
                details.push({
                    label: t("chatMessages.detailOutput"),
                    value: t("chatMessages.dataExtracted"),
                });
            }
            break;

        case "start_code_generation":
            if (args?.generationType) {
                details.push({
                    label: t("chatMessages.detailType"),
                    value: args.generationType.replace(/_/g, " "),
                });
            }
            if (args?.summary) {
                details.push({
                    label: t("chatMessages.detailPlan"),
                    value: args.summary,
                });
            }
            break;

        case "line_replace":
            if (args?.fileName) {
                details.push({
                    label: t("chatMessages.detailFile"),
                    value: args.fileName,
                });
            }
            if (args?.firstLine && args?.lastLine) {
                details.push({
                    label: t("chatMessages.detailLines"),
                    value: `${args.firstLine}-${args.lastLine}`,
                });
            }
            if (result?.linesRemoved !== undefined) {
                details.push({
                    label: t("chatMessages.detailChanged"),
                    value: `Removed ${result.linesRemoved}, added ${
                        result.linesAdded || 0
                    } lines`,
                });
            }
            break;

        case "edit_file":
            if (args?.file_path) {
                details.push({
                    label: t("chatMessages.detailFile"),
                    value: args.file_path.split("/").pop(),
                });
            }
            if (args?.replace_all) {
                details.push({
                    label: t("chatMessages.detailMode"),
                    value: t("chatMessages.replaceAll"),
                });
            }
            if (result?.occurrences_replaced !== undefined) {
                details.push({
                    label: t("chatMessages.detailChanged"),
                    value: `${result.occurrences_replaced} occurrence${
                        result.occurrences_replaced !== 1 ? "s" : ""
                    } replaced`,
                });
            }
            break;

        case "rename_file":
            if (args?.oldFilePath) {
                details.push({
                    label: t("chatMessages.detailFrom"),
                    value: args.oldFilePath.split("/").pop(),
                });
            }
            if (args?.newFilePath) {
                details.push({
                    label: t("chatMessages.detailTo"),
                    value: args.newFilePath.split("/").pop(),
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            break;

        case "search_in_files":
            if (args?.searchTerm) {
                details.push({
                    label: t("chatMessages.detailSearch"),
                    value: `"${args.searchTerm}"`,
                });
            }
            if (result?.filesWithMatches !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.totalMatches || 0} matches in ${
                        result.filesWithMatches
                    } files`,
                });
            }
            break;

        case "read_application_logs":
            if (args?.log_type) {
                details.push({
                    label: t("chatMessages.detailType"),
                    value: args.log_type,
                });
            }
            if (args?.url_pattern) {
                details.push({
                    label: t("chatMessages.detailPattern"),
                    value: args.url_pattern,
                });
            }
            if (args?.search_term) {
                details.push({
                    label: t("chatMessages.detailSearch"),
                    value: args.search_term,
                });
            }
            if (args?.method) {
                details.push({
                    label: t("chatMessages.detailMode"),
                    value: args.method,
                });
            }
            if (result?.count !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.count} logs`,
                });
            }
            break;

        case "explore_codebase":
            // Show subagent type first
            details.push({
                label: t("chatMessages.detailType"),
                value: t("chatMessages.exploreCodebase"),
            });
            if (args?.query) {
                details.push({
                    label: t("chatMessages.detailTask"),
                    value: args.query,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            if (args?.thoroughness) {
                details.push({
                    label: t("chatMessages.detailDepth"),
                    value:
                        args.thoroughness.charAt(0).toUpperCase() +
                        args.thoroughness.slice(1),
                });
            }
            if (args?.scope) {
                details.push({
                    label: t("chatMessages.detailScope"),
                    value: args.scope,
                });
            }
            if (args?.file_pattern) {
                details.push({
                    label: t("chatMessages.detailPattern"),
                    value: args.file_pattern,
                });
            }
            // Result details
            if (result?.filesSearchedCount !== undefined) {
                details.push({
                    label: t("chatMessages.detailFilesSearched"),
                    value: `${result.filesSearchedCount} files`,
                });
            }
            if (result?.filesSearched?.length > 0) {
                const fileList =
                    result.filesSearched.length <= 5
                        ? result.filesSearched.join(", ")
                        : `${result.filesSearched.slice(0, 5).join(", ")} +${
                              result.filesSearched.length - 5
                          } more`;
                details.push({
                    label: t("chatMessages.detailFiles"),
                    value: fileList,
                });
            }
            if (result?.matchesFound !== undefined) {
                details.push({
                    label: t("chatMessages.detailMatches"),
                    value: `${result.matchesFound} found`,
                });
            }
            if (result?.iterationsUsed !== undefined) {
                details.push({
                    label: t("chatMessages.detailIterations"),
                    value: `${result.iterationsUsed} tool calls`,
                });
            }
            if (result?.answerPreview) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.answerPreview,
                });
            }
            break;

        case "view_version_diff":
            if (result?.fromVersion && result?.toVersion) {
                details.push({
                    label: t("chatMessages.detailComparing"),
                    value: `v${result.fromVersion} -> v${result.toVersion}`,
                });
            }
            if (args?.steps_back !== undefined) {
                details.push({
                    label: t("chatMessages.detailStepsBack"),
                    value: `${args.steps_back}`,
                });
            }
            if (args?.component_name) {
                details.push({
                    label: t("chatMessages.detailComponent"),
                    value: args.component_name,
                });
            }
            if (result?.totalAdditions !== undefined) {
                details.push({
                    label: t("chatMessages.detailChanges"),
                    value: `+${result.totalAdditions} -${result.totalDeletions} lines`,
                });
            }
            if (result?.components?.length > 0) {
                details.push({
                    label: t("chatMessages.detailFiles"),
                    value: result.components
                        .map((c) => `${c.name} (${c.status})`)
                        .join(", "),
                });
            }
            break;

        case "search_api":
            // Show subagent type first
            details.push({
                label: t("chatMessages.detailType"),
                value: t("chatMessages.apiSearchType"),
            });
            if (args?.objective) {
                details.push({
                    label: t("chatMessages.detailTask"),
                    value: args.objective,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            // Result details
            if (result?.promptsSearched !== undefined) {
                details.push({
                    label: t("chatMessages.detailSearched"),
                    value: `${result.promptsSearched} API examples`,
                });
            }
            if (result?.apisFound !== undefined) {
                details.push({
                    label: t("chatMessages.detailApisFound"),
                    value: `${result.apisFound} APIs`,
                });
            }
            if (result?.availableApis?.length > 0) {
                const apiNames = result.availableApis
                    .map(
                        (api) =>
                            `${api.name}${
                                api.endpointCount
                                    ? ` (${api.endpointCount} endpoints)`
                                    : ""
                            }`
                    )
                    .join(", ");
                details.push({
                    label: t("chatMessages.detailAvailable"),
                    value: apiNames,
                });
            }
            if (result?.answerPreview) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.answerPreview,
                });
            }
            break;

        case "search_web":
            // Show subagent type first
            details.push({
                label: t("chatMessages.detailType"),
                value: t("chatMessages.webSearchType"),
            });
            if (args?.query) {
                details.push({
                    label: t("chatMessages.detailTask"),
                    value: args.query,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            if (result?.tokensUsed !== undefined) {
                const totalTokens =
                    (result.tokensUsed.input || 0) +
                    (result.tokensUsed.output || 0);
                details.push({
                    label: t("chatMessages.detailTokens"),
                    value: `${totalTokens} used`,
                });
            }
            if (result?.answerPreview) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.answerPreview,
                });
            }
            break;

        // Automation Agent tools
        case "inspect_step_data":
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            if (result?.step?.name) {
                details.push({
                    label: t("chatMessages.detailStepName"),
                    value: result.step.name,
                });
            }
            break;

        case "inspect_execution_logs":
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            if (result?.step?.name) {
                details.push({
                    label: t("chatMessages.detailStepName"),
                    value: result.step.name,
                });
            }
            break;

        case "read_step_code":
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            if (result?.step?.name) {
                details.push({
                    label: t("chatMessages.detailStepName"),
                    value: result.step.name,
                });
            }
            break;

        case "edit_step_code":
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            if (args?.description) {
                details.push({
                    label: t("chatMessages.detailDescription"),
                    value: args.description,
                });
            }
            break;

        case "grep_step_code":
            if (args?.pattern) {
                details.push({
                    label: t("chatMessages.detailSearch"),
                    value: `"${args.pattern}"`,
                });
            }
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            break;

        case "generate_code":
            if (args?.description) {
                details.push({
                    label: t("chatMessages.detailDescription"),
                    value: args.description,
                });
            }
            if (result?.node_metadata?.title) {
                details.push({
                    label: t("chatMessages.detailStepName"),
                    value: result.node_metadata.title,
                });
            }
            break;

        case "create_step":
            if (args?.name) {
                details.push({
                    label: t("chatMessages.detailStepName"),
                    value: args.name,
                });
            }
            if (args?.description) {
                details.push({
                    label: t("chatMessages.detailDescription"),
                    value: args.description,
                });
            }
            break;

        case "list_steps":
            if (result?.steps?.length !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.steps.length} steps`,
                });
            }
            break;

        case "delete_step":
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            break;

        case "update_step_metadata":
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            break;

        case "preview_execution":
            if (args?.step_identifier) {
                details.push({
                    label: t("chatMessages.detailStep"),
                    value: args.step_identifier,
                });
            }
            break;

        case "generate_trigger":
            if (args?.description) {
                details.push({
                    label: t("chatMessages.detailDescription"),
                    value: args.description,
                });
            }
            break;

        case "list_triggers":
            if (result?.triggers?.length !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.triggers.length} triggers`,
                });
            }
            break;

        case "search_tools":
            if (args?.query) {
                details.push({
                    label: t("chatMessages.detailQuery"),
                    value: args.query,
                });
            }
            if (result?.message) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.message,
                });
            }
            break;

        case "list_secrets":
            if (result?.secrets?.length !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.secrets.length} secrets`,
                });
            }
            break;

        case "request_secret":
            if (args?.secret_name) {
                details.push({
                    label: t("chatMessages.detailSecretName"),
                    value: args.secret_name,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            if (result?.message) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.message,
                });
            }
            break;

        case "fetch_documentation":
            if (args?.url) {
                details.push({
                    label: t("chatMessages.detailUrl"),
                    value: args.url,
                });
            }
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            break;

        case "enter_plan_mode":
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            break;

        case "exit_plan_mode":
            if (args?.reason) {
                details.push({
                    label: t("chatMessages.detailWhy"),
                    value: args.reason,
                });
            }
            break;

        case "report_user_frustration":
            if (result?.message) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.message,
                });
            }
            break;

        case "testing_agent":
            if (args?.instruction) {
                details.push({
                    label: t("chatMessages.detailTask"),
                    value: args.instruction,
                });
            }
            if (result?.message) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.message,
                });
            }
            break;

        case "view_chat_history":
            if (args?.mode) {
                details.push({
                    label: t("chatMessages.detailMode"),
                    value: args.mode,
                });
            }
            if (args?.step_chat_id) {
                details.push({
                    label: t("chatMessages.detailSession"),
                    value: args.step_chat_id,
                });
            }
            if (args?.search || args?.grep) {
                details.push({
                    label: t("chatMessages.detailSearch"),
                    value: args.search || args.grep,
                });
            }
            if (result?.totalSessions !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.totalSessions} sessions`,
                });
            }
            if (result?.totalMessages !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.totalMatching || result.totalMessages} messages`,
                });
            }
            break;

        case "search_creative_solutions":
            if (args?.query) {
                details.push({
                    label: t("chatMessages.detailQuery"),
                    value: args.query,
                });
            }
            if (result?.message) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.message,
                });
            }
            break;

        case "explore_spreadsheet":
            if (args?.operation) {
                details.push({
                    label: t("chatMessages.detailOperation"),
                    value: args.operation,
                });
            }
            if (args?.fileUrl) {
                details.push({
                    label: t("chatMessages.detailFile"),
                    value: args.fileUrl,
                });
            }
            if (args?.sheet) {
                details.push({
                    label: t("chatMessages.detailSheet"),
                    value: args.sheet,
                });
            }
            if (result?.totalRows !== undefined) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: `${result.totalRows} rows, ${result.totalColumns} columns`,
                });
            }
            if (result?.matchCount !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.matchCount} matches`,
                });
            }
            break;

        case "explore_database":
            if (args?.operation) {
                details.push({
                    label: t("chatMessages.detailOperation"),
                    value: args.operation,
                });
            }
            if (args?.entityName) {
                details.push({
                    label: t("chatMessages.detailEntity"),
                    value: args.entityName,
                });
            }
            if (args?.tableName) {
                details.push({
                    label: t("chatMessages.detailTable"),
                    value: args.tableName,
                });
            }
            if (args?.source) {
                details.push({
                    label: t("chatMessages.detailSource"),
                    value: args.source,
                });
            }
            if (result?.entities?.length !== undefined) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: t("chatMessages.detailEntitiesFound", {
                        count: result.entities.length,
                    }),
                });
            }
            if (result?.rowCount !== undefined) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: t("chatMessages.detailRowsReturned", {
                        count: result.rowCount,
                    }),
                });
            }
            if (result?.totalMatches !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.totalMatches} matches`,
                });
            }
            break;

        case "create_records":
            if (args?.table) {
                details.push({
                    label: t("chatMessages.detailTable"),
                    value: args.table,
                });
            }
            if (result?.count !== undefined) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: `${result.count} records created`,
                });
            }
            break;

        case "search_api_prompts":
            if (args?.query) {
                details.push({
                    label: t("chatMessages.detailQuery"),
                    value: args.query,
                });
            }
            if (result?.message) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.message,
                });
            }
            break;

        case "report_planned_items":
            if (args?.summary) {
                details.push({
                    label: t("chatMessages.detailPlan"),
                    value: args.summary,
                });
            }
            break;

        case "read_skill":
            if (args?.skill_name) {
                details.push({
                    label: t("chatMessages.detailSkill"),
                    value: args.skill_name,
                });
            }
            break;

        case "list_skills":
            if (result?.skills?.length !== undefined) {
                details.push({
                    label: t("chatMessages.detailFound"),
                    value: `${result.skills.length} skills`,
                });
            }
            break;

        default:
            // Generic fallback
            if (result?.message) {
                details.push({
                    label: t("chatMessages.detailResult"),
                    value: result.message,
                });
            }
    }

    return details;
};

// ToolActivityMessage component - displays tool_call and tool_response in user-friendly format
const ToolActivityMessage = ({
    message,
    linkedResponse,
    onOpenMarkdown,
    components,
}) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const toolInfo = getToolDisplayInfo(message.toolName, t);
    const IconComponent = toolInfo.icon;

    // Determine if this is a completed action (has response)
    const isCompleted = !!linkedResponse;
    const isSuccess = linkedResponse?.functionResponse?.success !== false;

    // Get a summary of what was done (uses snapshot data)
    const getSummary = () => {
        const args = message.functionCall || {};
        const result = linkedResponse?.functionResponse || {};

        // File operations - show file name from snapshot
        if (args.fileName) {
            return args.fileName;
        }

        // edit_file uses file_path instead of fileName
        if (args.file_path) {
            return args.file_path.split("/").pop();
        }

        // Search operations - show search term
        if (args.searchTerm || args.pattern || args.query) {
            return `"${args.searchTerm || args.pattern || args.query}"`;
        }

        // Files array (already just file names in snapshot)
        if (args.files && Array.isArray(args.files)) {
            if (args.files.length === 1) {
                return args.files[0];
            }
            return `${args.files.length} files`;
        }

        // Result message
        if (result.message) {
            return result.message;
        }

        return null;
    };

    const summary = getSummary();

    // Check if this is a markdown file operation
    const args = message.functionCall || {};
    const fileName =
        args.fileName ||
        (args.file_path ? args.file_path.split("/").pop() : null);
    const filePath = args.fileName || args.file_path;
    const isMarkdownFile =
        fileName &&
        (fileName.endsWith(".md") || fileName.endsWith(".markdown"));

    // Try to get content from args first, then from components
    let markdownContent = args.code || args.content || args.new_string;
    if (!markdownContent && isMarkdownFile && components?.length > 0) {
        const matchingComponent = components.find(
            (c) =>
                c.filePath?.endsWith(fileName) ||
                c.name === fileName ||
                c.filePath === filePath
        );
        if (matchingComponent) {
            markdownContent = matchingComponent.code;
        }
    }

    const handleMarkdownClick = (e) => {
        e.stopPropagation();
        if (isMarkdownFile && markdownContent && onOpenMarkdown) {
            onOpenMarkdown(fileName, markdownContent);
        }
    };
    const details = formatToolDetails(
        message.toolName,
        message.functionCall,
        linkedResponse?.functionResponse,
        t
    );

    // Light, subtle color classes
    const colorClasses = {
        indigo: {
            text: "text-indigo-600",
            bg: "bg-indigo-50",
            border: "border-indigo-200",
        },
        emerald: {
            text: "text-emerald-600",
            bg: "bg-emerald-50",
            border: "border-emerald-200",
        },
        teal: {
            text: "text-teal-600",
            bg: "bg-teal-50",
            border: "border-teal-200",
        },
        red: {
            text: "text-red-600",
            bg: "bg-red-50",
            border: "border-red-200",
        },
        pink: {
            text: "text-pink-600",
            bg: "bg-pink-50",
            border: "border-pink-200",
        },
        orange: {
            text: "text-orange-600",
            bg: "bg-orange-50",
            border: "border-orange-200",
        },
        blue: {
            text: "text-blue-600",
            bg: "bg-blue-50",
            border: "border-blue-200",
        },
        amber: {
            text: "text-amber-600",
            bg: "bg-amber-50",
            border: "border-amber-200",
        },
        cyan: {
            text: "text-cyan-600",
            bg: "bg-cyan-50",
            border: "border-cyan-200",
        },
        violet: {
            text: "text-violet-600",
            bg: "bg-violet-50",
            border: "border-violet-200",
        },
        purple: {
            text: "text-purple-600",
            bg: "bg-purple-50",
            border: "border-purple-200",
        },
        gray: {
            text: "text-gray-600",
            bg: "bg-gray-50",
            border: "border-gray-200",
        },
    };

    const colors = colorClasses[toolInfo.color] || colorClasses.gray;

    if (message.toolName === "start_code_generation" || message.toolName === "request_user_action") {
        return null;
    }

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-3 w-full min-w-0 overflow-hidden"
        >
            <div
                className={`${colors.bg} rounded-lg px-3 py-1.5 text-xs border ${colors.border} overflow-hidden`}
            >
                <div className="flex items-center gap-2 w-full min-w-0">
                    <IconComponent
                        className={`h-3.5 w-3.5 ${colors.text} flex-shrink-0`}
                    />
                    <span
                        className={`${colors.text} font-medium flex-shrink-0`}
                    >
                        {toolInfo.label}
                    </span>
                    {summary && (
                        <span
                            className={`text-gray-600 truncate flex-1 min-w-0 ${
                                isMarkdownFile && markdownContent
                                    ? "cursor-pointer hover:text-blue-600 hover:underline"
                                    : ""
                            }`}
                            onClick={
                                isMarkdownFile && markdownContent
                                    ? handleMarkdownClick
                                    : undefined
                            }
                        >
                            {summary}
                        </span>
                    )}
                    {isCompleted &&
                        (isSuccess ? (
                            <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        ) : (
                            <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                        ))}
                    {!isCompleted && (
                        <Loader2 className="h-3 w-3 text-gray-400 animate-spin flex-shrink-0" />
                    )}
                    {details.length > 0 && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        >
                            {isExpanded ? (
                                <ChevronUp className="h-3 w-3" />
                            ) : (
                                <ChevronDown className="h-3 w-3" />
                            )}
                        </button>
                    )}
                </div>

                <AnimatePresence>
                    {isExpanded && details.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-2 pt-2 border-t border-gray-200 text-[11px] space-y-1"
                        >
                            {details.map((detail, idx) => (
                                <div key={idx} className="flex gap-2 min-w-0">
                                    <span className="text-gray-400 flex-shrink-0">
                                        {detail.label}:
                                    </span>
                                    <span className="text-gray-600 break-words min-w-0">
                                        {detail.value}
                                    </span>
                                </div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

// ThinkingIndicator component - shows "Thinking" with expandable details
const ThinkingIndicator = ({ elapsedTime, partialFeedback }) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    // Check for browser agent feedback (session means we have a live preview)
    const browserAgentFeedback =
        partialFeedback?.filter(
            (item) =>
                item.type?.startsWith("browser_agent_") ||
                item.toolName === "browser_agent"
        ) || [];
    const hasBrowserPreview = browserAgentFeedback.some(
        (f) => f.type === "browser_agent_session"
    );

    // Get a simple summary of what's happening
    const getLatestAction = () => {
        if (!partialFeedback || partialFeedback.length === 0) return null;
        const latest = partialFeedback[partialFeedback.length - 1];

        // Map types to user-friendly descriptions
        const actionMap = {
            file_reading: t("chatMessages.thinkingLookingAtFiles"),
            grep_search: t("chatMessages.thinkingSearchingInFiles"),
            glob_search: t("chatMessages.thinkingFindingFiles"),
            file_search: t("chatMessages.thinkingFindingFiles"),
            line_replace: t("chatMessages.thinkingMakingChanges"),
            edit_file: t("chatMessages.thinkingEditingFile"),
            code_generation: t("chatMessages.thinkingWritingCode"),
            component_creation: t("chatMessages.thinkingBuildingComponent"),
            file_deletion: t("chatMessages.thinkingRemovingFile"),
            read_application_logs: t("chatMessages.thinkingCheckingLogs"),
            api_search: t("chatMessages.thinkingLookingUpApis"),
            code_execution: t("chatMessages.thinkingRunningCode"),
            planning: t("chatMessages.thinkingPlanning"),
            generation_start: t("chatMessages.thinkingStarting"),
            todo_write: t("chatMessages.thinkingUpdatingTasks"),
            explore_codebase: t("chatMessages.thinkingInvokingSubagent"),
            search_api: t("chatMessages.thinkingInvokingSubagent"),
            search_web: t("chatMessages.thinkingSearchingWeb"),
            browser_agent: t("chatMessages.thinkingBrowserAgentRunning"),
            browser_agent_session: t(
                "chatMessages.thinkingBrowserAgentRunning"
            ),
            browser_agent_step: t("chatMessages.thinkingBrowserAgentRunning"),
            // AutomationAgent feedback types
            data_inspection: t("chatMessages.thinkingInspectingData"),
            log_inspection: t("chatMessages.thinkingCheckingLogs"),
            code_read: t("chatMessages.thinkingReadingStepCode"),
            code_edit: t("chatMessages.thinkingEditingStepCode"),
            code_search: t("chatMessages.thinkingSearchingStepCode"),
            step_creation: t("chatMessages.thinkingCreatingStep"),
            step_listing: t("chatMessages.thinkingListingSteps"),
            step_deletion: t("chatMessages.thinkingDeletingStep"),
            metadata_update: t("chatMessages.thinkingUpdatingMetadata"),
            execution_preview: t("chatMessages.thinkingPreviewingExecution"),
            trigger_generation: t("chatMessages.thinkingGeneratingTrigger"),
            trigger_listing: t("chatMessages.thinkingListingTriggers"),
            platform_qa: t("chatMessages.thinkingAnsweringQuestion"),
            tool_search: t("chatMessages.thinkingSearchingTools"),
            secret_listing: t("chatMessages.thinkingListingSecrets"),
            secret_request: t("chatMessages.thinkingRequestingSecret"),
            doc_fetch: t("chatMessages.thinkingFetchingDocs"),
            plan_mode: t("chatMessages.thinkingChangingMode"),
            frustration_report: t("chatMessages.thinkingReportingFrustration"),
            testing_agent: t("chatMessages.thinkingRunningTests"),
            view_chat_history: t("chatMessages.thinkingViewingChatHistory"),
            search_creative_solutions: t("chatMessages.thinkingSearchingSolutions"),
            explore_spreadsheet: t("chatMessages.thinkingExploringSpreadsheet"),
            explore_database: t("chatMessages.thinkingExploringDatabase"),
            create_records: t("chatMessages.thinkingCreatingRecords"),
            search_api_prompts: t("chatMessages.thinkingSearchingApiPrompts"),
            report_planned_items: t("chatMessages.thinkingReportingPlan"),
            read_skill: t("chatMessages.thinkingReadingSkill"),
            list_skills: t("chatMessages.thinkingListingSkills"),
        };

        // Check type first
        if (actionMap[latest.type]) {
            return actionMap[latest.type];
        }

        // For unknown type, check toolName as fallback
        if (latest.type === "unknown" && latest.toolName) {
            return actionMap[latest.toolName] || t("chatMessages.working");
        }

        return t("chatMessages.working");
    };

    const latestAction = getLatestAction();
    const hasDetails = partialFeedback && partialFeedback.length > 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-3 mb-4"
        >
            <div className="flex flex-col gap-3">
                {/* Browser Agent Preview - shown prominently above the working indicator */}
                {hasBrowserPreview && (
                    <BrowserAgentPreview feedback={browserAgentFeedback} />
                )}

                <div className="bg-gray-100 rounded-lg p-3 max-w-md">
                    {/* Main thinking indicator */}
                    <div className="flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-amber-500" />
                        <span className="text-sm text-gray-700 font-medium">
                            {latestAction || t("chatMessages.thinking")}
                        </span>
                        {elapsedTime > 0 && (
                            <span className="text-xs text-gray-400">
                                {elapsedTime}s
                            </span>
                        )}

                        {/* Expand/collapse button */}
                        {hasDetails && (
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="ml-auto text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                            >
                                {isExpanded ? (
                                    <>
                                        {t("chatMessages.hide")}
                                        <ChevronUp className="h-3 w-3" />
                                    </>
                                ) : (
                                    <>
                                        {t("chatMessages.showAll")}
                                        <ChevronDown className="h-3 w-3" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Expandable details section */}
                    <AnimatePresence>
                        {isExpanded && hasDetails && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="mt-3 pt-3 border-t border-gray-200"
                            >
                                <CodeGenerationFeedback
                                    feedback={partialFeedback}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

const MessageComponent = ({
    message,
    index,
    handleButtonClick,
    onOpenModal,
    linkedResponse,
    onOpenMarkdown,
    onImageClick,
    markdownFilesMap,
    components,
    isButtonDisabled = false,
    showHeader = true,
    showTimestamp = true,
    groupCreditsUsed,
}) => {
    const { t } = useTranslation();
    const isUser = message.role === "human" || message.role === "user";
    const isSystem = message.role === "system" || message.role === "assistant";
    const isCompacted = message.role === "compact";
    const isDiff = message.role === "diff";
    const isToolCall = message.role === "tool_call";
    const isToolResponse = message.role === "tool_response";
    const hasFile = message.file || message.fileAttachment;
    const hasContent = message.content && message.content.trim().length > 0;

    // Render tool_call messages with user-friendly ToolActivityMessage
    if (isToolCall) {
        return (
            <ToolActivityMessage
                message={message}
                linkedResponse={linkedResponse}
                onOpenMarkdown={onOpenMarkdown}
                components={components}
            />
        );
    }

    // Skip rendering tool_response (already shown with its tool_call)
    if (isToolResponse) {
        return null;
    }

    // Skip rendering assistant messages with no displayable content
    const hasFiles = message.files && message.files.length > 0;
    const hasButton = message.buttonText && message.action;
    if (
        isSystem &&
        !hasContent &&
        !hasFile &&
        !hasFiles &&
        !hasButton &&
        !isDiff
    ) {
        return null;
    }

    if (isDiff) {
        return <DiffDisplay diff={message.content} onOpenModal={onOpenModal} />;
    }

    return (
        <>
            {isCompacted && (
                <div className="w-full flex items-center justify-center mb-2">
                    <div className="flex items-center gap-2 text-gray-400 text-xs">
                        <Minus className="h-3 w-3" />
                        <span className="bg-gray-200 h-px flex-1"></span>
                        <span className="px-2 bg-white">
                            {t("chatMessages.compactedMessages")}
                        </span>
                        <span className="bg-gray-200 h-px flex-1"></span>
                        <Minus className="h-3 w-3" />
                    </div>
                </div>
            )}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 mb-6 min-w-0 group ${
                    isUser ? "justify-end" : "justify-start"
                }`}
            >
                {/* AI message: AgentUI logo, name, and timestamp on the left */}

                <div className="flex flex-col gap-1 min-w-0">
                    {!isUser && isSystem && showHeader && (
                        <div className="flex flex-col items-start gap-0.5 flex-shrink-0 pt-0.5 mb-1">
                            <div className="flex items-center gap-2">
                                <img
                                    src="/favicon-32x32.png"
                                    alt="AgentUI"
                                    className="h-7 w-7 rounded-full object-contain flex-shrink-0"
                                />
                                <span className="text-sm font-semibold text-gray-900 hidden sm:inline">
                                    AgentUI
                                </span>
                            </div>
                        </div>
                    )}
                    <div
                        className={`
                       max-w-[100%]
                     ${
                         isUser
                             ? "bg-blue-100/80 border border-blue-200/60 text-gray-900 rounded-2xl rounded-br-sm max-w-[95%]"
                             : isSystem
                             ? `${getSystemBgColor(message.role)}`
                             : isCompacted
                             ? "bg-orange-50 text-orange-800 rounded-lg border border-orange-200 border-dashed"
                             : isDiff
                             ? "p-0"
                             : "text-gray-900"
                     } ${
                            isUser ? "px-4 py-3" : "px-1 py-1"
                        } break-words min-w-0 ${
                            isCompacted
                                ? "relative overflow-visible"
                                : isUser
                                ? "relative overflow-x-hidden"
                                : "overflow-x-hidden"
                        }`}
                        style={PRE_STYLE}
                    >
                        {/* Revert button floating on user messages */}
                        {isUser && message.workflowStepVersionId && (
                            <div className="absolute bottom-1 right-1 z-10">
                                <RollbackButton messageId={message.id} />
                            </div>
                        )}
                        {/* Compacted Message Badge */}
                        {isCompacted && (
                            <div className="absolute -top-2 -left-2 bg-orange-100 border border-orange-200 rounded-full p-1 z-10">
                                <Archive className="h-3 w-3 text-orange-600" />
                            </div>
                        )}
                        {/* Message Text */}
                        {!isDiff && hasContent ? (
                            <div
                                className="text-[15px] leading-relaxed overflow-x-hidden"
                                style={BREAK_WORD_STYLE}
                            >
                                <Markdown
                                    remarkPlugins={[remarkGfm]}
                                    urlTransform={safeUrlTransform}
                                    components={{
                                        a: ({ node, ...props }) => (
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline break-all"
                                                {...props}
                                            />
                                        ),
                                        img: ({ node, ...props }) => (
                                            <ClickableImage
                                                src={props.src}
                                                alt={props.alt}
                                                onImageClick={onImageClick}
                                            />
                                        ),
                                        p: ({ children }) => (
                                            <p className="mb-3 last:mb-0 leading-relaxed">
                                                {children}
                                            </p>
                                        ),
                                        pre: ({ children }) => (
                                            <pre className="bg-gray-50 text-gray-700 border border-gray-200 p-4 rounded-xl my-3 text-sm font-mono whitespace-pre-wrap break-all leading-relaxed">
                                                {children}
                                            </pre>
                                        ),
                                        code: (props) => {
                                            const {
                                                node,
                                                className,
                                                children,
                                                ...rest
                                            } = props;

                                            // Check if inline code is a markdown file reference
                                            const codeText =
                                                String(children).trim();
                                            const isMarkdownRef =
                                                codeText.endsWith(".md") ||
                                                codeText.endsWith(".markdown");

                                            // O(1) lookup using pre-computed map
                                            const matchingComponent =
                                                isMarkdownRef
                                                    ? markdownFilesMap?.get(
                                                          codeText.toLowerCase()
                                                      )
                                                    : null;

                                            if (matchingComponent) {
                                                // Handle clickable markdown file reference
                                                return (
                                                    <code
                                                        className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-sm font-mono border border-blue-300 cursor-pointer hover:bg-blue-200 transition-colors"
                                                        style={BREAK_WORD_STYLE}
                                                        onClick={() =>
                                                            onOpenMarkdown &&
                                                            onOpenMarkdown(
                                                                codeText,
                                                                matchingComponent.code
                                                            )
                                                        }
                                                        {...rest}
                                                    >
                                                        {children}
                                                    </code>
                                                );
                                            }

                                            // Handle inline code - improved contrast and readability
                                            return (
                                                <code
                                                    className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-md text-[13px] font-mono border border-gray-200"
                                                    style={BREAK_WORD_STYLE}
                                                    {...rest}
                                                >
                                                    {children}
                                                </code>
                                            );
                                        },
                                        h1: ({ children }) => (
                                            <h1 className="text-xl font-bold mb-3 mt-4 first:mt-0">
                                                {children}
                                            </h1>
                                        ),
                                        h2: ({ children }) => (
                                            <h2 className="text-lg font-bold mb-2 mt-4 first:mt-0">
                                                {children}
                                            </h2>
                                        ),
                                        h3: ({ children }) => (
                                            <h3 className="text-base font-bold mb-2 mt-3 first:mt-0">
                                                {children}
                                            </h3>
                                        ),
                                        ul: ({ children }) => (
                                            <ul className="list-disc mb-2 space-y-0.5 ml-4">
                                                {children}
                                            </ul>
                                        ),
                                        ol: ({ children }) => (
                                            <ol className="list-decimal mb-2 space-y-0.5 ml-4">
                                                {children}
                                            </ol>
                                        ),
                                        li: ({ children }) => (
                                            <li className="leading-relaxed pl-2">
                                                {children}
                                            </li>
                                        ),
                                        blockquote: ({ children }) => (
                                            <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2">
                                                {children}
                                            </blockquote>
                                        ),
                                        strong: ({ children }) => (
                                            <strong className="font-semibold">
                                                {children}
                                            </strong>
                                        ),
                                        em: ({ children }) => (
                                            <em className="italic">
                                                {children}
                                            </em>
                                        ),
                                        table: ({ children }) => (
                                            <div className="overflow-x-auto my-2">
                                                <table className="min-w-full border-collapse border border-gray-300 text-sm">
                                                    {children}
                                                </table>
                                            </div>
                                        ),
                                        thead: ({ children }) => (
                                            <thead className="bg-gray-100">
                                                {children}
                                            </thead>
                                        ),
                                        tbody: ({ children }) => (
                                            <tbody>{children}</tbody>
                                        ),
                                        tr: ({ children }) => (
                                            <tr className="border-b border-gray-300">
                                                {children}
                                            </tr>
                                        ),
                                        th: ({ children }) => (
                                            <th className="border border-gray-300 px-3 py-2 text-left font-semibold">
                                                {children}
                                            </th>
                                        ),
                                        td: ({ children }) => (
                                            <td className="border border-gray-300 px-3 py-2">
                                                {children}
                                            </td>
                                        ),
                                    }}
                                >
                                    {message.content || ""}
                                </Markdown>
                            </div>
                        ) : null}

                        {/* File Card - Show when message has file attachment */}
                        {hasFile &&
                            (() => {
                                const file =
                                    message.file || message.fileAttachment;
                                const isImage =
                                    file?.type?.startsWith("image/");
                                const imgSrc = file?.url || file?.preview;
                                return (
                                    <div className={hasContent ? "mt-3" : ""}>
                                        {isImage && imgSrc ? (
                                            <ClickableImage
                                                src={imgSrc}
                                                alt={file.name}
                                                onImageClick={onImageClick}
                                            />
                                        ) : (
                                            <FileCard file={file} />
                                        )}
                                    </div>
                                );
                            })()}

                        {/* Multiple Files - Show when message has files array */}
                        {message.files &&
                            message.files.length > 0 &&
                            (() => {
                                const imageFiles = message.files.filter(
                                    (f) =>
                                        f?.type?.startsWith("image/") &&
                                        (f?.url || f?.preview)
                                );
                                const nonImageFiles = message.files.filter(
                                    (f) =>
                                        !f?.type?.startsWith("image/") ||
                                        !(f?.url || f?.preview)
                                );
                                return (
                                    <div className={hasContent ? "mt-3" : ""}>
                                        <div className="space-y-2">
                                            {imageFiles.length > 0 && (
                                                <ImageGalleryPreview
                                                    images={imageFiles.map(
                                                        (f) => ({
                                                            src:
                                                                f.url ||
                                                                f.preview,
                                                            alt: f.name,
                                                        })
                                                    )}
                                                    onImageClick={onImageClick}
                                                />
                                            )}
                                            {nonImageFiles.length > 0 && (
                                                <FileListPreview
                                                    files={nonImageFiles}
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                        {/* Diff Display - Show when message has diff data */}
                        {isDiff && message.content && (
                            <DiffDisplay
                                diff={message.content}
                                onOpenModal={onOpenModal}
                            />
                        )}

                        {/* Message Button - Show when message has buttonText and action */}
                        {message.buttonText && message.action && (
                            <div className="mt-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        handleButtonClick(message.action)
                                    }
                                    disabled={isButtonDisabled}
                                    className="text-xs"
                                >
                                    {message.buttonText}
                                </Button>
                            </div>
                        )}

                        {/* Language Badge - Show when available and not null */}
                        {message.language && message.language !== "none" && (
                            <div className="mt-2">
                                <Badge
                                    variant="outline"
                                    className="text-xs bg-gray-100 text-gray-700 border-gray-300"
                                >
                                    {message.language === "html" ? (
                                        <Globe className="h-3 w-3 mr-1" />
                                    ) : (
                                        <Code className="h-3 w-3 mr-1" />
                                    )}
                                    {message.language === "html"
                                        ? "Web"
                                        : "Automation"}
                                </Badge>
                            </div>
                        )}

                        {/* User Message Metadata */}
                        {isUser && (
                            <div className="mt-2 space-y-1">
                                {/* Skills */}
                                {message.skills &&
                                    message.skills.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {message.skills.map((skill) => (
                                                <Badge
                                                    key={skill.id}
                                                    variant="secondary"
                                                    className="text-xs bg-blue-100 text-blue-700 border-blue-200"
                                                >
                                                    <span
                                                        className="mr-1"
                                                        role="img"
                                                        aria-label="Skill icon"
                                                    >
                                                        {skill.icon}
                                                    </span>
                                                    {skill.name}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                {/* Integration */}
                                {message.integration?.length > 0 && (
                                    <div className="flex items-center gap-1">
                                        <Badge
                                            variant="secondary"
                                            className="text-xs bg-blue-100 text-blue-700 border-blue-200"
                                        >
                                            {message.integration.imageIcon ? (
                                                <img
                                                    src={
                                                        message.integration
                                                            .imageIcon
                                                    }
                                                    alt={
                                                        message.integration.name
                                                    }
                                                    className="h-3 w-3 mr-1"
                                                />
                                            ) : (
                                                <Icon
                                                    name={
                                                        message.integration.icon
                                                    }
                                                    className="h-3 w-3 mr-1"
                                                />
                                            )}
                                            {message.integration.name}
                                        </Badge>
                                    </div>
                                )}

                                {/* Workflow Information */}
                                {message.workflow?.length > 0 && (
                                    <div className="flex items-center gap-1">
                                        <Badge
                                            variant="secondary"
                                            className="text-xs bg-purple-100 text-purple-700 border-purple-200"
                                        >
                                            <span
                                                className="mr-1"
                                                role="img"
                                                aria-label="Workflow icon"
                                            >
                                                ⚡
                                            </span>
                                            {t("chatMessages.workflow")}{" "}
                                            {message.workflow.name ||
                                                message.workflow.id}
                                        </Badge>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Relative time below message (e.g. "2 days ago") */}
                    {message.createdAt && showTimestamp && (
                        <div
                            className={`flex items-center gap-1.5 mt-2 mb-4 ${
                                isUser ? "justify-end mr-1" : "ml-1"
                            }`}
                        >
                            <p className="text-xs text-gray-400">
                                {formatDistanceToNow(
                                    new Date(message.createdAt),
                                    {
                                        addSuffix: true,
                                    }
                                )}
                            </p>
                            {isSystem && (
                                <MessageUsageDropdown
                                    creditsUsed={
                                        groupCreditsUsed ?? message.creditsUsed
                                    }
                                    llmModel={message.llmModel}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* {isUser && (
                <div className="flex-shrink-0 ">
                    <User className="h-6 w-6 text-blue-600" />
                </div>
            )} */}
            </motion.div>
        </>
    );
};

// Memoize the MessageComponent for better performance
const MemoizedMessageComponent = memo(MessageComponent);

const ChatMessages = memo(() => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { workflow } = useWorkflow();
    const {
        chatMessages,
        chatHelperMessages,
        clearHelperMessages,
        isTyping,
        isGeneratingCode,
        activeStep,
        setActiveStep,
        isChatLoading,
        handleAction,
        isCompactingChatMessages,

        partialFeedback,
        tasks,
        setTasks,
        startNewChat,
        chatRating,
        handleRateChat,
        selectedStepChatId,
        stepsConfig,
        chatMode,
        humanSupportMessages,
        fetchOlderChatMessages,
        hasMoreChatMessages,
        userActionRequest,
    } = useWorkflowGeneration();

    // Use human support messages when in human_support mode, otherwise use regular chat messages
    const displayMessages =
        chatMode === "human_support" ? humanSupportMessages : chatMessages;

    const [expandedCompactIds, setExpandedCompactIds] = useState(new Set());
    const [lightboxImage, setLightboxImage] = useState(null);
    const [runtimeDismissed, setRuntimeDismissed] = useState(
        () => localStorage.getItem("runtimeSuggestionsDismissed") === "true"
    );
    const { traceMessages, actionMessages } = useMemo(() => {
        const trace = [];
        const action = [];
        if (chatHelperMessages) {
            for (const m of chatHelperMessages) {
                if (m.action) action.push(m);
                else trace.push(m);
            }
        }
        return { traceMessages: trace, actionMessages: action };
    }, [chatHelperMessages]);
    const handleDismissRuntime = useCallback(() => {
        setRuntimeDismissed(true);
        localStorage.setItem("runtimeSuggestionsDismissed", "true");
    }, []);
    const handleRestoreRuntime = useCallback(() => {
        setRuntimeDismissed(false);
        localStorage.setItem("runtimeSuggestionsDismissed", "false");
    }, []);
    const [visibleTraceCount, setVisibleTraceCount] = useState(10);
    const [visibleActionCount, setVisibleActionCount] = useState(10);
    const [hideRating, setHideRating] = useState(false);
    const initialRatingRef = useRef(null);

    // Track if chat was loaded with a rating - hide rating UI in that case
    useEffect(() => {
        initialRatingRef.current = chatRating;
        setHideRating(!!chatRating);
    }, [selectedStepChatId]);

    // After user rates, show for 7 seconds then hide
    useEffect(() => {
        if (chatRating && initialRatingRef.current !== chatRating) {
            const timer = setTimeout(() => {
                setHideRating(true);
            }, 7000);
            return () => clearTimeout(timer);
        }
    }, [chatRating]);

    // Check if AI has responded
    const hasAiResponse = React.useMemo(() => {
        return chatMessages.some(
            (msg) => msg.role === "assistant" || msg.role === "system"
        );
    }, [chatMessages]);

    const toggleCompactExpanded = useCallback((messageId) => {
        setExpandedCompactIds((prev) => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    }, []);

    const { selectedEnvironment, openMarkdownPreview, components, fetchComponents } =
        useChatContext();

    // Auto-refresh component list when AI finishes generating code
    const wasGeneratingRef = useRef(false);
    const lastRefreshRef = useRef(0);
    useEffect(() => {
        if (isGeneratingCode) {
            wasGeneratingRef.current = true;
        } else if (wasGeneratingRef.current) {
            wasGeneratingRef.current = false;
            const now = Date.now();
            if (now - lastRefreshRef.current > 2000) {
                lastRefreshRef.current = now;
                fetchComponents();
            }
        }
    }, [isGeneratingCode, fetchComponents]);

    // Memoized lookup map for markdown files - O(1) lookup instead of O(n) search per code element
    const markdownFilesMap = React.useMemo(() => {
        const map = new Map();
        if (!components?.length) return map;

        components.forEach((c) => {
            const filePath = (c.filePath || "").toLowerCase();
            const name = (c.name || "").toLowerCase();

            // Only index markdown files
            if (
                !filePath.endsWith(".md") &&
                !filePath.endsWith(".markdown") &&
                !name.endsWith(".md") &&
                !name.endsWith(".markdown")
            ) {
                return;
            }

            // Index by various patterns that might be used to reference the file
            // e.g., "README.md", "docs/README.md", full path
            const fileName = filePath.split("/").pop();
            if (fileName) map.set(fileName, c);
            if (name) map.set(name, c);
            if (filePath) map.set(filePath, c);

            // Also index without leading slash if present
            if (filePath.startsWith("/")) {
                map.set(filePath.slice(1), c);
            }
        });

        return map;
    }, [components]);

    const { taskStatus, generationStartTime } = useCodeGeneration();
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isDiffModalOpen, setIsDiffModalOpen] = useState(false);
    const [selectedDiff, setSelectedDiff] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const messagesEndRef = useRef(null);
    const scrollAreaRef = useRef(null);
    const activeStepRefDid = useRef(null);
    const previousScrollHeight = useRef(0);
    const isAtBottom = useRef(true);
    const isInitialLoad = useRef(true);
    const lastMessageRef = useRef(null);

    const handleUpdateTask = (taskId, updates) => {
        setTasks((prev) =>
            prev.map((t) => (t.id === taskId ? { ...t, ...updates } : t))
        );
    };

    const handleAddTask = (content) => {
        const newTask = {
            id: Date.now().toString(),
            content,
            status: "pending",
        };
        setTasks((prev) => [...prev, newTask]);
    };

    const handleDeleteTask = (taskId) => {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
    };

    // Modal handlers
    const handleOpenDiffModal = (diff) => {
        setSelectedDiff(diff);
        setIsDiffModalOpen(true);
    };

    const handleCloseDiffModal = () => {
        setIsDiffModalOpen(false);
        setSelectedDiff(null);
    };

    const handleActionWithEnvironment = useCallback(
        (action) => {
            handleAction(action, selectedEnvironment);
        },
        [handleAction, selectedEnvironment]
    );

    // Handle scroll to load more messages from server
    const handleScroll = useCallback(
        (event) => {
            const viewport = event.target;
            const scrollTop = viewport.scrollTop;
            const scrollHeight = viewport.scrollHeight;
            const clientHeight = viewport.clientHeight;

            // Check if user is at the bottom (within 50px threshold)
            isAtBottom.current = scrollHeight - scrollTop - clientHeight < 50;

            // Check if user scrolled to the top (within 50px threshold)
            if (scrollTop < 50 && !isLoadingMore && hasMoreChatMessages) {
                setIsLoadingMore(true);
                previousScrollHeight.current = scrollHeight;
                fetchOlderChatMessages().finally(() => {
                    setIsLoadingMore(false);
                });
            }
        },
        [isLoadingMore, hasMoreChatMessages, fetchOlderChatMessages]
    );

    // Restore scroll position after loading more messages (skip on initial load)
    useEffect(() => {
        if (
            scrollAreaRef.current &&
            previousScrollHeight.current > 0 &&
            !isInitialLoad.current
        ) {
            const viewport = scrollAreaRef.current.querySelector(
                "[data-radix-scroll-area-viewport]"
            );
            if (viewport) {
                const newScrollHeight = viewport.scrollHeight;
                const scrollDiff =
                    newScrollHeight - previousScrollHeight.current;
                viewport.scrollTop = scrollDiff;
                previousScrollHeight.current = 0;
            }
        }
    }, [displayMessages.length]);

    // Helper function to scroll to bottom
    const scrollToBottom = useCallback((behavior = "smooth") => {
        if (scrollAreaRef.current) {
            const viewport = scrollAreaRef.current.querySelector(
                "[data-radix-scroll-area-viewport]"
            );
            if (viewport) {
                viewport.scrollTo({
                    top: viewport.scrollHeight,
                    behavior: behavior,
                });
            }
        }
    }, []);

    // Auto-scroll to bottom when messages change or typing state changes
    // Always scroll when: new user message, code generation starts, or user is at bottom
    useEffect(() => {
        // Check if a new user message was added
        const lastMessage = displayMessages[displayMessages.length - 1];
        const isNewUserMessage =
            lastMessage &&
            (lastMessage.role === "human" ||
                lastMessage.role === "human_support_user") &&
            lastMessage.id !== lastMessageRef.current;

        // Update the last message ref
        if (lastMessage) {
            lastMessageRef.current = lastMessage.id;
        }

        // Always scroll when: user sends message, generating code, or user is at bottom
        if (isNewUserMessage || isGeneratingCode || isAtBottom.current) {
            // When user sends a message, always scroll and mark as at bottom
            if (isNewUserMessage) {
                isAtBottom.current = true;
            }

            // Use setTimeout to ensure DOM has updated before scrolling
            const scrollBehavior = isInitialLoad.current ? "instant" : "smooth";
            setTimeout(() => {
                scrollToBottom(scrollBehavior);
            }, 50);

            // Mark initial load as complete after first scroll
            if (isInitialLoad.current) {
                setTimeout(() => {
                    isInitialLoad.current = false;
                }, 100);
            }
        }
    }, [
        displayMessages,
        chatHelperMessages,
        isTyping,
        isGeneratingCode,
        isCompactingChatMessages,
        partialFeedback,
        scrollToBottom,
    ]);

    // Attach scroll listener
    useEffect(() => {
        const scrollArea = scrollAreaRef.current;
        if (scrollArea) {
            const viewport = scrollArea.querySelector(
                "[data-radix-scroll-area-viewport]"
            );
            if (viewport) {
                viewport.addEventListener("scroll", handleScroll);
                return () => {
                    viewport.removeEventListener("scroll", handleScroll);
                };
            }
        }
    }, [handleScroll]);

    useEffect(() => {
        if (isGeneratingCode && generationStartTime) {
            const interval = setInterval(() => {
                const now = Date.now();
                const elapsed = Math.floor((now - generationStartTime) / 1000);
                setElapsedTime(elapsed);
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [isGeneratingCode, generationStartTime]);

    // Scroll to bottom when code generation starts
    useEffect(() => {
        if (isGeneratingCode) {
            // Small delay to ensure the code generation indicator is rendered
            setTimeout(() => {
                scrollToBottom("smooth");
            }, 100);
        }
    }, [isGeneratingCode, scrollToBottom]);

    useEffect(() => {
        if (
            (activeStep == null || activeStep === "") &&
            workflow &&
            workflow?.steps &&
            workflow?.steps?.length > 0 &&
            !activeStepRefDid.current
        ) {
            setActiveStep(workflow.steps[0].id);
            activeStepRefDid.current = true;
        }

        // Reset initial load flag when active step changes
        if (activeStep) {
            isInitialLoad.current = true;
            isAtBottom.current = true;
            lastMessageRef.current = null;
        }
    }, [workflow, activeStep, setActiveStep]);

    const visibleMessages = displayMessages;

    if (
        !displayMessages.length > 0 &&
        !chatHelperMessages?.length > 0 &&
        !isTyping &&
        !isGeneratingCode &&
        !isChatLoading
    ) {
        return (
            <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                    <Bot className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">
                        {t("chatMessages.startConversation")}
                    </p>
                    <p className="text-sm">
                        {t("chatMessages.selectSkillsDescription")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col h-full min-w-0 relative overflow-hidden">
            {/* {chatMessages.length > 0 && (
                <button
                    onClick={archiveStepChatForStep}
                    className="absolute top-2 right-2 z-10 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-800 border border-gray-200 hover:border-gray-300 rounded-full p-2 shadow-sm transition-all duration-200 group"
                    title="Start a new chat"
                    aria-label="Reset chat"
                >
                    <Pencil className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
                </button>
            )} */}

            <ScrollArea
                ref={scrollAreaRef}
                className="flex-1 h-0 overflow-x-hidden min-w-0 w-full"
            >
                <div className="p-6 space-y-2 w-full min-w-0 max-w-3xl mx-auto overflow-hidden">
                    {/* Load More Indicator */}
                    {hasMoreChatMessages && (
                        <div className="flex justify-center py-2">
                            {isLoadingMore ? (
                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span>{t("chatMessages.loadingMore")}</span>
                                </div>
                            ) : (
                                <button
                                    onClick={() => {
                                        setIsLoadingMore(true);
                                        previousScrollHeight.current =
                                            scrollAreaRef.current?.querySelector(
                                                "[data-radix-scroll-area-viewport]"
                                            )?.scrollHeight || 0;
                                        fetchOlderChatMessages().finally(() => {
                                            setIsLoadingMore(false);
                                        });
                                    }}
                                    className="text-gray-500 text-xs hover:text-gray-700 hover:underline cursor-pointer"
                                >
                                    {t("chatMessages.scrollUpForMore")}
                                </button>
                            )}
                        </div>
                    )}

                    {visibleMessages.map((message, index) => {
                        // Find linked response for tool_call messages
                        let linkedResponse = null;
                        if (
                            message.role === "tool_call" &&
                            message.toolCallId
                        ) {
                            linkedResponse = visibleMessages.find(
                                (m) =>
                                    m.role === "tool_response" &&
                                    m.toolCallId === message.toolCallId
                            );
                        }

                        // Special rendering for human support messages
                        if (
                            message.role === "human_support_user" ||
                            message.role === "human_support_staff" ||
                            message.role === "human_support_system"
                        ) {
                            return (
                                <HumanSupportMessage
                                    key={
                                        message.id ||
                                        message.ticketMessageId ||
                                        `human-support-${index}`
                                    }
                                    message={message}
                                    onImageClick={setLightboxImage}
                                />
                            );
                        }

                        // Special rendering for compact messages
                        if (message.role === "compact") {
                            const messageId =
                                message.id || `compact-${index}`;
                            const isExpanded =
                                expandedCompactIds.has(messageId);

                            return (
                                <div key={messageId} className="w-full">
                                    {/* Collapsible line with toggle */}
                                    <div
                                        className="w-full flex items-center justify-center my-3 cursor-pointer group"
                                        onClick={() =>
                                            toggleCompactExpanded(messageId)
                                        }
                                    >
                                        <div className="flex-1 h-px bg-gray-300 group-hover:bg-orange-300 transition-colors"></div>
                                        <div className="flex items-center gap-2 px-3 py-1 text-xs text-gray-500 group-hover:text-orange-600 transition-colors">
                                            <Archive className="h-3 w-3" />
                                            <span>
                                                {t(
                                                    "chatMessages.compactedMessages"
                                                )}
                                            </span>
                                            {isExpanded ? (
                                                <ChevronUp className="h-3 w-3" />
                                            ) : (
                                                <ChevronDown className="h-3 w-3" />
                                            )}
                                        </div>
                                        <div className="flex-1 h-px bg-gray-300 group-hover:bg-orange-300 transition-colors"></div>
                                    </div>

                                    {/* Expandable content */}
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{
                                                    opacity: 0,
                                                    height: 0,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    height: "auto",
                                                }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="bg-orange-50 border border-orange-200 border-dashed rounded-lg p-3 mb-4">
                                                    <div
                                                        className="text-sm text-orange-800"
                                                        style={BREAK_WORD_STYLE}
                                                    >
                                                        <Markdown breaks={true} urlTransform={safeUrlTransform}>
                                                            {message.content || ""}
                                                        </Markdown>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        }

                        const isAiRole = (role) =>
                            role === "system" ||
                            role === "assistant" ||
                            role === "tool_call" ||
                            role === "tool_response";
                        const isAiMessage =
                            message.role === "system" ||
                            message.role === "assistant";
                        const prevMessage =
                            index > 0 ? visibleMessages[index - 1] : null;
                        const nextMessage =
                            index < visibleMessages.length - 1
                                ? visibleMessages[index + 1]
                                : null;
                        const showHeader =
                            !isAiMessage ||
                            !prevMessage ||
                            !isAiRole(prevMessage.role);
                        const showTimestamp =
                            !isAiMessage ||
                            !nextMessage ||
                            !isAiRole(nextMessage.role);

                        // Sum credits across consecutive AI messages in the group
                        let groupCreditsUsed = undefined;
                        if (showTimestamp && isAiMessage) {
                            let total = 0;
                            for (let i = index; i >= 0; i--) {
                                const m = visibleMessages[i];
                                if (!isAiRole(m.role)) break;
                                if (m.creditsUsed) total += m.creditsUsed;
                            }
                            if (total > 0) groupCreditsUsed = total;
                        }

                        return (
                            <MemoizedMessageComponent
                                key={message.id || index}
                                message={message}
                                index={index}
                                handleButtonClick={handleActionWithEnvironment}
                                onOpenModal={handleOpenDiffModal}
                                linkedResponse={linkedResponse}
                                onOpenMarkdown={openMarkdownPreview}
                                onImageClick={setLightboxImage}
                                markdownFilesMap={markdownFilesMap}
                                components={components}
                                showHeader={showHeader}
                                showTimestamp={showTimestamp}
                                groupCreditsUsed={groupCreditsUsed}
                            />
                        );
                    })}

                    {tasks.length > 0 && (
                        <div className="mx-auto w-full max-w-3xl">
                            <TaskList
                                tasks={tasks}
                                onUpdateTask={handleUpdateTask}
                                onAddTask={handleAddTask}
                                onDeleteTask={handleDeleteTask}
                                disabled={isGeneratingCode}
                            />
                        </div>
                    )}

                    {/* Runtime suggestions from console.human — hidden while generating */}
                    {!isGeneratingCode &&
                        chatHelperMessages?.length > 0 &&
                        runtimeDismissed && (
                            <div
                                className="flex items-center gap-2 py-1 cursor-pointer group"
                                onClick={handleRestoreRuntime}
                            >
                                <div className="flex-1 h-px bg-gray-200 group-hover:bg-gray-300 transition-colors" />
                                <span className="text-xs text-gray-400 group-hover:text-gray-500 transition-colors flex items-center gap-1">
                                    <Bot className="h-3 w-3" />
                                    {t("chatMessages.appSignals")} (
                                    {chatHelperMessages.length})
                                    <ChevronDown className="h-3 w-3" />
                                </span>
                                <div className="flex-1 h-px bg-gray-200 group-hover:bg-gray-300 transition-colors" />
                            </div>
                        )}
                    {!isGeneratingCode &&
                        chatHelperMessages?.length > 0 &&
                        !runtimeDismissed && (
                            <>
                                {traceMessages.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex gap-3 mb-6 justify-start"
                                    >
                                        <div className="flex flex-col gap-1 min-w-0 w-full">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Bot className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                                <span className="text-xs text-gray-400">
                                                    {t(
                                                        "chatMessages.appSignals"
                                                    )}
                                                </span>
                                                <div className="ml-auto flex items-center gap-1">
                                                    <button
                                                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                                        onClick={
                                                            clearHelperMessages
                                                        }
                                                    >
                                                        {t("common.clear")}
                                                    </button>
                                                    <button
                                                        className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                                                        onClick={
                                                            handleDismissRuntime
                                                        }
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="px-1 py-1 space-y-1">
                                                {traceMessages
                                                    .slice(0, visibleTraceCount)
                                                    .map((message, index) => (
                                                        <div
                                                            key={`trace-${index}`}
                                                        >
                                                            <p
                                                                className="text-sm leading-relaxed text-gray-500"
                                                                style={
                                                                    BREAK_WORD_STYLE
                                                                }
                                                            >
                                                                {
                                                                    message.content
                                                                }
                                                            </p>
                                                            {message.data &&
                                                                typeof message.data ===
                                                                    "object" && (
                                                                    <RuntimeDataTable
                                                                        data={
                                                                            message.data
                                                                        }
                                                                    />
                                                                )}
                                                        </div>
                                                    ))}
                                                {traceMessages.length > visibleTraceCount && (
                                                    <button
                                                        className="text-xs text-gray-400 hover:text-gray-500 transition-colors py-1 w-full text-center"
                                                        onClick={() => setVisibleTraceCount((c) => c + 10)}
                                                    >
                                                        {t("common.showMore")} ({traceMessages.length - visibleTraceCount} {t("common.more")})
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                {actionMessages.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex gap-3 mb-6 justify-start"
                                    >
                                        <div className="flex flex-col gap-1 min-w-0 w-full">
                                            {traceMessages.length === 0 && (
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Bot className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                                    <span className="text-xs text-gray-400">
                                                        {t(
                                                            "chatMessages.appRuntime"
                                                        )}
                                                    </span>
                                                    <div className="ml-auto flex items-center gap-1">
                                                        <button
                                                            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                                            onClick={
                                                                clearHelperMessages
                                                            }
                                                        >
                                                            {t("common.clear")}
                                                        </button>
                                                        <button
                                                            className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                                                            onClick={
                                                                handleDismissRuntime
                                                            }
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="space-y-3">
                                                {actionMessages
                                                    .slice(0, visibleActionCount)
                                                    .map((message, index) => (
                                                        <div
                                                            key={`action-${
                                                                message.id ||
                                                                index
                                                            }`}
                                                            className="px-1 py-1"
                                                        >
                                                            <p
                                                                className="text-[15px] leading-relaxed text-gray-700"
                                                                style={
                                                                    BREAK_WORD_STYLE
                                                                }
                                                            >
                                                                {
                                                                    message.content
                                                                }
                                                            </p>
                                                            <div className="mt-2">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        handleActionWithEnvironment(
                                                                            message.action
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        isGeneratingCode
                                                                    }
                                                                    className="text-xs"
                                                                >
                                                                    {
                                                                        message.buttonText
                                                                    }
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                {actionMessages.length > visibleActionCount && (
                                                    <button
                                                        className="text-xs text-gray-400 hover:text-gray-500 transition-colors py-1 w-full text-center"
                                                        onClick={() => setVisibleActionCount((c) => c + 10)}
                                                    >
                                                        {t("common.showMore")} ({actionMessages.length - visibleActionCount} {t("common.more")})
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </>
                        )}

                    {/* User Action Request — interactive UI from AI */}
                    {userActionRequest && (
                        <div className="mb-4">
                            <img
                                src="/favicon-32x32.png"
                                alt="AgentUI"
                                className="h-5 w-5 mb-2"
                            />
                            <UserActionRequest
                                key={
                                    userActionRequest.requestId ||
                                    `${userActionRequest.type}-${
                                        userActionRequest.questions?.length ?? 0
                                    }-${userActionRequest.options?.length ?? 0}`
                                }
                                request={userActionRequest}
                                onAction={handleActionWithEnvironment}
                                isGeneratingCode={isGeneratingCode}
                            />
                        </div>
                    )}

                    {/* Typing Indicator */}
                    {isTyping ||
                        (isChatLoading && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex gap-3 mb-6"
                            >
                                <img
                                    src="/favicon-32x32.png"
                                    alt="AgentUI"
                                    className="h-7 w-7 rounded-full object-contain flex-shrink-0"
                                />
                                <div className="py-2">
                                    <div className="flex gap-1.5">
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                        <div
                                            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                            style={{ animationDelay: "0.1s" }}
                                        ></div>
                                        <div
                                            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                            style={{ animationDelay: "0.2s" }}
                                        ></div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                    {/* Compacting Messages Indicator */}
                    {isCompactingChatMessages && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex gap-3 mb-4"
                        >
                            <div className="flex-shrink-0">
                                <Archive className="h-6 w-6 text-orange-600" />
                            </div>
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 max-w-xs">
                                <div className="flex items-center gap-2 mb-2">
                                    <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
                                    <span className="text-orange-700 text-sm font-medium">
                                        {t("chatMessages.compactingMessages")}
                                    </span>
                                </div>
                                <div className="text-xs text-orange-600">
                                    {t("chatMessages.compactingDescription")}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Long Thread Nudge */}
                    {!isGeneratingCode &&
                        !isTyping &&
                        !user?.tutorials?.long_thread_tip?.completed &&
                        chatMessages.length >= 35 && (
                            <LongThreadNudge onStartNewChat={startNewChat} />
                        )}

                    {/* Code Generation Indicator */}
                    {isGeneratingCode && (
                        <ThinkingIndicator
                            elapsedTime={elapsedTime}
                            partialFeedback={partialFeedback}
                        />
                    )}

                    {/* Chat Rating */}
                    {hasAiResponse &&
                        !isGeneratingCode &&
                        !isTyping &&
                        !hideRating && (
                            <div className="flex items-center justify-center gap-2 py-2 border-t border-gray-100 mt-2">
                                <span className="text-xs text-gray-500 mr-2">
                                    {chatRating
                                        ? t("chatMessages.yourRating")
                                        : t("chatMessages.rateThisChat")}
                                </span>
                                <button
                                    onClick={() => handleRateChat(1)}
                                    className={`p-1.5 rounded-full transition-all ${
                                        chatRating === 1
                                            ? "bg-red-100 text-red-600 ring-2 ring-red-300"
                                            : "hover:bg-red-50 text-gray-400 hover:text-red-500"
                                    }`}
                                    title={t("chatMessages.bad")}
                                >
                                    <ThumbsDown className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleRateChat(2)}
                                    className={`p-1.5 rounded-full transition-all ${
                                        chatRating === 2
                                            ? "bg-yellow-100 text-yellow-600 ring-2 ring-yellow-300"
                                            : "hover:bg-yellow-50 text-gray-400 hover:text-yellow-500"
                                    }`}
                                    title={t("chatMessages.okay")}
                                >
                                    <Meh className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleRateChat(3)}
                                    className={`p-1.5 rounded-full transition-all ${
                                        chatRating === 3
                                            ? "bg-green-100 text-green-600 ring-2 ring-green-300"
                                            : "hover:bg-green-50 text-gray-400 hover:text-green-500"
                                    }`}
                                    title={t("chatMessages.great")}
                                >
                                    <ThumbsUp className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                    {/* Invisible element to scroll to */}
                    <div ref={messagesEndRef} />
                </div>
            </ScrollArea>

            {/* Full-screen Diff Modal */}
            <DiffModal
                isOpen={isDiffModalOpen}
                onClose={handleCloseDiffModal}
                selectedDiff={selectedDiff}
            />

            {/* Single shared image lightbox */}
            <ImageLightboxDialog
                image={lightboxImage}
                onClose={() => setLightboxImage(null)}
            />
        </div>
    );
});

export default ChatMessages;
